import * as React from 'react';

import { darken, transparentize } from 'polished';
import styled from 'styled-components';

import { COLOR, FONT_SIZE, FONT_STACK, RADIUS, SPACING } from '../../packages/constants';
import { HelperModal, QuestionMark } from '../helper-modal';

interface StyledSelectField {
    invalid?: boolean;
    emphasize?: boolean;
    hasLegacyStyles?: boolean;
    width?: string;
    minWidth?: string;
    maxWidth?: string;
    thinLabel?: boolean;
}

export interface SelectFieldProps {
    dataId: string;
    id: string;
    name: string;
    options: SelectFieldOptions;
    value?: string;
    onChange?: (e: any) => void;
    disabled?: boolean;
    hasHelper?: boolean;
    hasLegacyStyles?: boolean;
    helperContent?: React.ReactNode;
    invalid?: boolean;
    label?: string;
    placeholder?: string;
    emphasize?: boolean;
    width?: string;
    minWidth?: string;
    maxWidth?: string;
    thinLabel?: boolean;
}

export type SelectFieldOptions = SelectFieldOptionsItem[];

interface SelectFieldOptionsItem {
    name: string;
    value: string;
}

interface StyledTextLabelProps {
    disabled?: boolean;
    invalid?: boolean;
    emphasize?: boolean;
    hasLegacyStyles?: boolean;
    thinLabel?: boolean;
}

interface InputWrapperProps {
    width?: string;
    minWidth?: string;
    maxWidth?: string;
    disabled?: boolean;
    emphasize?: boolean;
}

const Wrapper = styled.label<StyledTextLabelProps>`
    display: flex;
    flex-direction: column;
`;

const InputWrapper = styled.div<InputWrapperProps>`
    display: inline-flex;
    position: relative;
    margin-right: auto;
    width: ${(props) => (props.width ? props.width : 'auto')};
    min-width: ${(props) => (props.minWidth ? props.minWidth : 0)};
    max-width: ${(props) => (props.maxWidth ? props.maxWidth : 'none')};

    &::after {
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-top: 6px solid
            ${(props) => (props.emphasize ? COLOR.DROPDOWN.DROPDOWN_ARROW_DARK : COLOR.DROPDOWN.DROPDOWN_ARROW)};
        content: '';
        position: absolute;
        bottom: ${SPACING.MEDIUM};
        right: ${SPACING.SMALL};
        opacity: ${(props) => (props.disabled ? '0.7' : '1')};
        pointer-events: none;
    }

    &:hover {
        &::after {
            border-top-color: ${(props: InputWrapperProps): string => (props.disabled ? '' : COLOR.BRAND.REPORTING)};
        }
    }
`;

const WrapperHasHelper = styled.div<StyledTextLabelProps>`
    display: flex;
    flex-direction: column;
    position: relative;
`;

const StyledLabelWrapper = styled.div`
    display: inline-flex;
`;

const StyledTextLabel = styled.span<StyledTextLabelProps>`
    display: block;
    padding-bottom: ${(props) => (props.hasLegacyStyles ? 0 : SPACING.XX_SMALL)};
    font-family: ${FONT_STACK};
    font-weight: ${(props: StyledTextLabelProps): string => (props.thinLabel ? '400' : '700')};
    font-size: ${(props) => (props.hasLegacyStyles ? FONT_SIZE.X_SMALL : FONT_SIZE.SMALL)};
    line-height: ${(props) => (props.hasLegacyStyles ? 'normal' : FONT_SIZE.MEDIUM)};
    margin-bottom: ${(props) => (props.hasLegacyStyles ? '8px' : 0)};
    color: ${(props) =>
        props.invalid ? COLOR.PRIMARY.RED : props.hasLegacyStyles ? COLOR.PRIMARY.BLACK : COLOR.MAIN.LABEL_TEXT};
    opacity: ${(props) => (props.disabled ? '0.7' : '1')};
`;

const StyledLabelHasHelper = styled.label<StyledTextLabelProps>`
    display: inline-block;
    padding-bottom: ${(props) => (props.hasLegacyStyles ? 0 : SPACING.XX_SMALL)};
    font-family: ${FONT_STACK};
    font-weight: ${(props: StyledTextLabelProps): string => (props.thinLabel ? '400' : '700')};
    font-size: ${(props) => (props.hasLegacyStyles ? FONT_SIZE.X_SMALL : FONT_SIZE.SMALL)};
    line-height: ${(props) => (props.hasLegacyStyles ? 'normal' : FONT_SIZE.MEDIUM)};
    margin-bottom: ${(props) => (props.hasLegacyStyles ? '8px' : 0)};
    margin-right: ${SPACING.X_SMALL};
    color: ${(props) =>
        props.invalid ? COLOR.PRIMARY.RED : props.hasLegacyStyles ? COLOR.PRIMARY.BLACK : COLOR.MAIN.LABEL_TEXT};
    opacity: ${(props) => (props.disabled ? '0.7' : '1')};
`;

const StyledSelectField = styled.select<StyledSelectField>`
    background-color: ${(props) => (props.disabled ? darken('0.1', COLOR.PRIMARY.WHITE) : COLOR.PRIMARY.WHITE)};
    color: ${(props) => (props.disabled ? transparentize('0.5', COLOR.PRIMARY.BLACK) : COLOR.PRIMARY.BLACK)};
    font-family: ${FONT_STACK};
    font-size: ${(props) => (props.hasLegacyStyles ? '13px' : FONT_SIZE.SMALL)};
    line-height: ${(props) => (props.hasLegacyStyles ? '18px' : FONT_SIZE.MEDIUM)};
    padding: ${SPACING.SMALL} ${SPACING.X_LARGE} ${SPACING.SMALL} ${SPACING.SMALL};
    border-width: 1px;
    border-style: solid;
    border-color: ${(props) =>
        props.invalid ? COLOR.PRIMARY.RED : props.emphasize ? COLOR.MAIN.FORM_FIELD_DARK : COLOR.MAIN.FORM_FIELD};
    border-radius: ${(props) => (props.hasLegacyStyles ? 0 : RADIUS.DEFAULT)};
    appearance: none;
    margin: 0;
    width: 100%;

    &:focus {
        box-shadow: 0 0 1px 3px rgba(59, 153, 252, 0.7);
        outline: none;
    }
`;

const StyledOption = styled.option`
    font-weight: normal;
`;

export const SelectInput = React.forwardRef<HTMLSelectElement, SelectFieldProps>(
    (
        {
            dataId,
            id,
            invalid,
            hasLegacyStyles,
            disabled,
            onChange,
            emphasize,
            width,
            minWidth,
            maxWidth,
            value,
            placeholder,
            options,
            ...props
        },
        ref,
    ) => (
        <InputWrapper disabled={disabled} width={width} minWidth={minWidth} maxWidth={maxWidth}>
            <StyledSelectField
                data-test-id={`${dataId}-select`}
                id={id}
                invalid={invalid}
                hasLegacyStyles={hasLegacyStyles}
                disabled={disabled}
                onChange={onChange}
                emphasize={emphasize}
                value={value}
                ref={ref}
                {...props}
            >
                {placeholder && <StyledOption value={''}>{placeholder}</StyledOption>}
                {options.map((item: any, i: number) => (
                    <StyledOption key={`${item.value}-${i}`} value={item.value}>
                        {item.name}
                    </StyledOption>
                ))}
            </StyledSelectField>
        </InputWrapper>
    ),
);
SelectInput.displayName = 'SelectInput';

export const SelectField = React.forwardRef<HTMLSelectElement, SelectFieldProps>(
    (
        {
            dataId,
            id,
            disabled,
            value,
            onChange,
            placeholder = '',
            invalid,
            label,
            options,
            hasHelper,
            hasLegacyStyles,
            helperContent,
            emphasize,
            thinLabel,
            ...props
        },
        ref,
    ) => {
        return hasHelper ? (
            <WrapperHasHelper emphasize={emphasize} {...props}>
                <StyledLabelWrapper>
                    <StyledLabelHasHelper
                        data-test-id={`${dataId}-label`}
                        disabled={disabled}
                        invalid={invalid}
                        htmlFor={id}
                        hasLegacyStyles={hasLegacyStyles}
                        thinLabel={thinLabel}
                    >
                        {label}
                    </StyledLabelHasHelper>
                    <HelperModal dataId={dataId} modalContent={helperContent}>
                        <QuestionMark />
                    </HelperModal>
                </StyledLabelWrapper>
                <SelectInput
                    dataId={dataId}
                    id={id}
                    options={options}
                    invalid={invalid}
                    hasLegacyStyles={hasLegacyStyles}
                    disabled={disabled}
                    onChange={onChange}
                    emphasize={emphasize}
                    value={value}
                    placeholder={placeholder}
                    ref={ref}
                    {...props}
                />
            </WrapperHasHelper>
        ) : (
            <Wrapper data-test-id={`${dataId}-label`} disabled={disabled} emphasize={emphasize} {...props}>
                {label && (
                    <StyledTextLabel
                        disabled={disabled}
                        invalid={invalid}
                        hasLegacyStyles={hasLegacyStyles}
                        thinLabel={thinLabel}
                    >
                        {label}
                    </StyledTextLabel>
                )}
                <SelectInput
                    dataId={dataId}
                    id={id}
                    options={options}
                    invalid={invalid}
                    hasLegacyStyles={hasLegacyStyles}
                    disabled={disabled}
                    onChange={onChange}
                    emphasize={emphasize}
                    value={value}
                    placeholder={placeholder}
                    ref={ref}
                    {...props}
                />
            </Wrapper>
        );
    },
);
SelectField.displayName = 'SelectField';
